import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BODY_ZONES, TATTOO_STYLES } from '../../hooks/useSessions';
import type { useSessionWizard } from '../../hooks/useSessionWizard';

type WizardProps = { wizard: ReturnType<typeof useSessionWizard> };

export default function Step5Materials({ wizard }: WizardProps) {
  // ─── Infos projet ───────────────────────────────────────────
  const [zoneGroup, setZoneGroup] = useState('');
  const [zone, setZone] = useState('');
  const [style, setStyle] = useState('');
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');

  // ─── Matériaux ──────────────────────────────────────────────
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const { data: auth } = await supabase.auth.getUser();
      const { data } = await supabase
        .from('products')
        .select('id, name, stock, category')
        .eq('owner_id', auth.user!.id)
        .order('name');
      return data ?? [];
    },
  });

  const zonesInGroup = zoneGroup ? (BODY_ZONES[zoneGroup] ?? []) : [];

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const materials = Object.entries(quantities)
        .filter(([, qty]) => qty > 0)
        .map(([productId, quantity]) => ({ productId, quantity: Number(quantity) }));

      await wizard.launchSession({
        projectName:  projectName,
        bodyZone:     zone || zoneGroup,
        style:        style,
        projectNotes: description,
        materials,
      });
      // navigation handled inside launchSession — component unmounts here
    } catch (err: any) {
      setError(err?.message ?? 'Erreur lors du démarrage');
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    setError(null);
    try {
      await wizard.launchSession({ projectName: '', bodyZone: '', style: '', projectNotes: '', materials: [] });
    } catch (err: any) {
      setError(err?.message ?? 'Erreur lors du démarrage');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Infos projet & matériaux</h2>

      {/* ── Bloc infos projet ── */}
      <div className="glass-card p-4 space-y-4">
        <p className="text-sm font-semibold text-foreground">Infos projet</p>

        {/* Nom / titre */}
        <div className="space-y-1.5">
          <Label htmlFor="projectName" className="text-sm">Titre du projet</Label>
          <Input
            id="projectName"
            placeholder="ex. Dragon japonais — 15×20 cm"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
          />
        </div>

        {/* Zone du corps — cascade groupe → zone */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm">Groupe</Label>
            <Select
              value={zoneGroup}
              onValueChange={(v) => { setZoneGroup(v); setZone(''); }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner…" />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(BODY_ZONES).map((g) => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">Zone précise</Label>
            <Select
              value={zone}
              onValueChange={setZone}
              disabled={!zoneGroup}
            >
              <SelectTrigger>
                <SelectValue placeholder={zoneGroup ? 'Choisir…' : '—'} />
              </SelectTrigger>
              <SelectContent>
                {zonesInGroup.map((z) => (
                  <SelectItem key={z} value={z}>{z}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Style */}
        <div className="space-y-1.5">
          <Label className="text-sm">Style</Label>
          <Select value={style} onValueChange={setStyle}>
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner un style…" />
            </SelectTrigger>
            <SelectContent>
              {TATTOO_STYLES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <Label htmlFor="description" className="text-sm">Description</Label>
          <Textarea
            id="description"
            placeholder="Détails du projet, références, demandes spécifiques…"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="resize-none"
          />
        </div>
      </div>

      {/* ── Bloc matériaux ── */}
      <div className="space-y-3">
        <p className="text-sm font-semibold text-foreground">
          Matériaux prévus
          <span className="ml-1 font-normal text-muted-foreground">(optionnel — ajustable à la clôture)</span>
        </p>

        {products.length === 0 ? (
          <p className="text-sm text-muted-foreground px-1">Aucun produit en stock.</p>
        ) : (
          <div className="divide-y rounded-xl border overflow-hidden">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-3 px-4 py-3">
                <span className="flex-1 text-sm font-medium">{p.name}</span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  Stock : {p.stock}
                </span>
                <Input
                  type="number"
                  min={0}
                  max={p.stock ?? undefined}
                  className="w-20 h-8 text-sm text-center"
                  value={quantities[p.id] ?? ''}
                  placeholder="0"
                  onChange={(e) =>
                    setQuantities((q) => ({ ...q, [p.id]: Number(e.target.value) }))
                  }
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-sm">{error}</div>
      )}

      {/* ── Actions ── */}
      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" onClick={handleSkip} disabled={loading}>
          Passer
        </Button>
        <Button className="flex-1 gap-2" onClick={handleConfirm} disabled={loading}>
          {loading && <Loader2 size={16} className="animate-spin" />}
          {loading ? 'Démarrage…' : 'Démarrer la session'}
        </Button>
      </div>
    </div>
  );
}
